'use client';

import { type ElementType } from 'react';

import { type VariantProps } from 'tailwind-variants';

import {
  type PolymorphicComponent,
  type PolymorphicComponentProps,
} from '../../types/component';
import { tv } from '../../utils/tv';

const surfaceClasses = { root: 'zds-surface-root' } as const;

type Variant = VariantProps<typeof surfaceStyles>;

const surfaceStyles = tv({
  base: [surfaceClasses.root, 'rounded-md'],
  variants: {
    variant: {
      outlined: 'border border-default-muted-1',
      elevated: '', // defined in compound variants
    },
    // defined in compound variants
    color: {
      neutral: '',
      primary: '',
    },
    // defined in compound variants
    layer: {
      3: '',
      2: '',
      1: '',
    },
    // defined in compound variants
    elevation: {
      1: '',
      2: '',
      3: '',
    },
  },
  compoundVariants: [
    { color: 'neutral', layer: 1, className: 'surface-neutral-layer-1' },
    { color: 'neutral', layer: 2, className: 'surface-neutral-layer-2' },
    { color: 'neutral', layer: 3, className: 'surface-neutral-layer-3' },
    /* eslint-disable better-tailwindcss/no-restricted-classes */
    {
      color: 'primary',
      layer: 1,
      className: 'surface-primary-layer-1',
    },
    {
      color: 'primary',
      layer: 2,
      className: 'surface-primary-layer-2',
    },
    {
      color: 'primary',
      layer: 3,
      className: 'surface-primary-layer-3',
    },
    /* eslint-enable better-tailwindcss/no-restricted-classes */

    { variant: 'elevated', elevation: 1, className: 'shadow-level-1' },
    { variant: 'elevated', elevation: 2, className: 'shadow-level-2' },
    { variant: 'elevated', elevation: 3, className: 'shadow-level-3' },
  ],
  defaultVariants: {
    variant: 'outlined',
    color: 'neutral',
    elevation: 1,
    layer: 3,
  },
});

interface SurfaceBaseProps {
  /**
   * The type of `Surface`, either `outlined` or `elevated`.
   * @default 'outlined'
   */
  variant?: Variant['variant'];
  /**
   * Determines background color of `Surface`.
   * @default 'neutral'
   * @deprecated Use `className` with Tailwind utility classes instead.
   */
  color?: Variant['color'];

  /**
   * Determines background color based on component depth. 1 is darkest, 3 is lightest.
   * @default 3
   */
  layer?: Variant['layer'];

  /**
   * Elevation, or drop shadow, for `Surface`. Only applies when `variant` is `elevated`.
   * @default 1
   */
  elevation?: Variant['elevation'];
}

type SurfaceProps<RootComponent extends ElementType = 'div'> =
  PolymorphicComponentProps<SurfaceBaseProps, RootComponent>;

type SurfaceComponent = PolymorphicComponent<SurfaceBaseProps, 'div'>;

const Surface: SurfaceComponent = (props) => {
  const {
    component: Root = 'div',
    variant = 'outlined',
    color = 'neutral',
    layer = 3,
    elevation = 1,
    className,
    ref,
    ...rest
  } = props;

  const classes = surfaceStyles({
    variant,
    color,
    layer,
    elevation,
    className,
  });

  return <Root ref={ref} className={classes} {...rest} />;
};

Surface.displayName = 'Surface';

export { Surface, type SurfaceProps, type SurfaceBaseProps, surfaceClasses };
